import { Component, lazy, Suspense, type ReactNode } from "react";
import { captureException } from "@sentry/react";

/** Ask Fin's code is downloaded only when it is shown, so brokers without the feature never load it. */
const AskFin = lazy(() => import("@/components/AskFin/AskFin").then((module) => ({ default: module.AskFin })));

interface LoadBoundaryState {
  hasFailed: boolean;
}

/**
 * Keeps a failed load (e.g. its file has gone after a deploy, or the network dropped) from taking down the page
 * around it: Ask Fin is simply not shown, and Sentry is told.
 */
class AskFinLoadBoundary extends Component<{ children: ReactNode }, LoadBoundaryState> {
  state: LoadBoundaryState = { hasFailed: false };

  static getDerivedStateFromError(): LoadBoundaryState {
    return { hasFailed: true };
  }

  componentDidCatch(error: unknown): void {
    captureException(error);
  }

  render(): ReactNode {
    return this.state.hasFailed ? null : this.props.children;
  }
}

/** Ask Fin, loaded on demand. Shows nothing while it loads, or if it cannot be loaded. */
const LazyAskFin = () => (
  <AskFinLoadBoundary>
    <Suspense fallback={null}>
      <AskFin />
    </Suspense>
  </AskFinLoadBoundary>
);

export { LazyAskFin };
